import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatDate } from "../lib/licenses";
import { ServerPagination } from "./LicenseTable";

const PAGE = 25;

const contact = (c) => [c.email, c.phone].filter(Boolean).join(" · ");

// rows: customer records (keyCount is kept by Cloud Functions).
// distributorName(id) adds a distributor column (admin view). `paging` as in
// LicenseTable for server-side pages.
export default function CustomerTable({
  rows,
  onRowClick,
  distributorName,
  resetKey,
  emptyText,
  paging,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [resetKey]);

  if (rows.length === 0 && !paging?.hasPrev) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 4, textAlign: "center", color: "text.secondary" }}
      >
        {emptyText}
      </Paper>
    );
  }

  const current = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / PAGE) - 1),
  );
  const visible = paging
    ? rows
    : rows.slice(current * PAGE, (current + 1) * PAGE);
  const pagination = paging ? (
    <ServerPagination paging={paging} />
  ) : rows.length > PAGE ? (
    <TablePagination
      component="div"
      count={rows.length}
      page={current}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={PAGE}
      rowsPerPageOptions={[PAGE]}
    />
  ) : null;

  if (isMobile) {
    return (
      <Box>
        {visible.map((c) => (
          <Card key={c.id} sx={{ mb: 1.25 }}>
            <CardActionArea onClick={() => onRowClick(c)}>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2">{c.name}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {c.keyCount || 0} {c.keyCount === 1 ? "key" : "keys"}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {[distributorName?.(c.distributorId), c.location, contact(c)]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
        {pagination}
      </Box>
    );
  }

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small" sx={{ "& td, & th": { py: 1.1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              {distributorName && <TableCell>Distributor</TableCell>}
              <TableCell>City / address</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="right">Keys</TableCell>
              <TableCell>Added</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((c) => (
              <TableRow
                key={c.id}
                hover
                onClick={() => onRowClick(c)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                {distributorName && (
                  <TableCell>{distributorName(c.distributorId)}</TableCell>
                )}
                <TableCell>{c.location || "—"}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {contact(c) || "—"}
                </TableCell>
                <TableCell align="right">{c.keyCount || 0}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(c.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </Paper>
  );
}
